/**
 * captureError — SSOT telemetry sink for caught errors.
 *
 * Previously the only path to Sentry was `ErrorBoundary.componentDidCatch`,
 * which covers render-time errors only. Every async/promise catch in
 * `src/modules/**` that wrote `console.error('… failed:', err)` bypassed
 * telemetry entirely (60+ sites). This helper unifies the three sinks
 * (Sentry, console, optional analytics) so service-layer catches can
 * surface to observability with one call.
 *
 * Usage:
 *   } catch (err) {
 *       captureError(err, { scope: 'NetworkingView', op: 'sendConnectionRequest' });
 *       toast.error('Could not send request');
 *   }
 *
 * Keep dependency-light (no React, no HTTP) — must be safe to import
 * from services, hooks, views, and the boundary itself.
 */

export type CaptureContext = {
    /** Module / view label, e.g. "NetworkingView" or "ErrorBoundary:Messages". */
    scope?: string;
    /** Operation that failed, e.g. "load-more PYMK" or "submit deliverables". */
    op?: string;
    /** Free-form extra payload (component stack, request id, etc.). */
    extra?: Record<string, unknown>;
};

type SentryLike = {
    captureException?: (err: unknown, ctx?: unknown) => void;
};

type AnalyticsLike = {
    track?: (event: string, payload?: Record<string, unknown>) => void;
};

function readSentry(): SentryLike | null {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as Record<string, unknown>;
    const s = w.__SENTRY__;
    return (s && typeof s === 'object') ? s as SentryLike : null;
}

function readAnalytics(): AnalyticsLike | null {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as Record<string, unknown>;
    const a = w.__ANALYTICS__;
    return (a && typeof a === 'object') ? a as AnalyticsLike : null;
}

function formatLabel(ctx: CaptureContext): string {
    const parts: string[] = [];
    if (ctx.scope) parts.push(ctx.scope);
    if (ctx.op) parts.push(ctx.op);
    return parts.length ? `[${parts.join(':')}]` : '[error]';
}

/**
 * Report a caught error to every wired telemetry sink.
 *
 * - Always logs to console.error so devtools shows the stack.
 * - Forwards to `window.__SENTRY__.captureException` when present.
 * - Forwards to `window.__ANALYTICS__.track('error', …)` when present.
 *
 * Returns void; never throws. Safe to call from any catch block.
 */
export function captureError(err: unknown, ctx: CaptureContext = {}): void {
    const label = formatLabel(ctx);

    // 1) Console — preserve devtools workflow.
     
    console.error(label, err);

    // 2) Sentry — the existing sink, now reachable from anywhere.
    // 3) Optional analytics — opt-in via window.__ANALYTICS__.
    //
    // Why: telemetry sinks must NOT propagate failure.
    // Both catches below are INTENTIONALLY silent (see captureError.ts:78):
    // if a telemetry sink itself throws (network outage, SDK init failure,
    // CSP block), we must NOT propagate that back to the caller's catch path
    // — otherwise an analytics outage would break every user-facing flow
    // that already went through its own error handling. This is the
    // canonical legitimate exception to the chiefy/no-silent-catch
    // contract; it is the ONE escape hatch the rule explicitly allows.
    // Why: telemetry sinks must NOT propagate failure (see captureError.ts:78).
    /* eslint-disable chiefy/no-silent-catch */
    try {
        const sentry = readSentry();
        if (sentry && typeof sentry.captureException === 'function') {
            sentry.captureException(err, {
                extra: { scope: ctx.scope, op: ctx.op, ...(ctx.extra ?? {}) },
            });
        }
    } catch {
        // Intentional swallow (see captureError.ts:78) — telemetry failure must not propagate.
    }
    try {
        const analytics = readAnalytics();
        if (analytics && typeof analytics.track === 'function') {
            analytics.track('error', {
                scope: ctx.scope,
                op: ctx.op,
                message: err instanceof Error ? err.message : String(err),
            });
        }
    } catch {
        // Intentional swallow (see captureError.ts:78) — telemetry failure must not propagate.
    }
    /* eslint-enable chiefy/no-silent-catch */
}
